import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createPasswords,
  devicesOfTypeQuery,
  PASS_SAFE_ORDER_NAMES,
  passSafeShellQuery,
  passSafeTableQuery,
  removePassword,
  revealPassword,
  searchDevices,
  updatePassword,
  type CredentialInput,
  type CredentialRole,
  type DeviceHit,
  type Option,
  type PassSafeFilters,
  type PassSafeShell,
} from '../../api/settingsPassSafe'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { ImportWithAiModal } from './ImportWithAiModal'
import './PassSafePage.css'

/**
 * Settings -> Auth Vault (Pass Safe), element cu element fata de
 * `views/version-3/settings/pass_safe/index.php`, `public/js/pass_safe.js` si
 * `public/js/pass_safe/new_password.js`.
 *
 * PAROLA NU E PE ECRAN PANA CAND CINEVA O CERE. Lista arata descrierea, contul,
 * cine a scris-o, aparatul si firma - niciodata parola, si nici macar textul ei
 * cifrat (in portal, cifratul venea cu fiecare rand, degeaba). Dezvaluirea e un
 * drum separat catre server, care verifica din nou drepturile pe parola aia si
 * scrie in jurnal cine a cerut-o.
 *
 * CE E ALTFEL FATA DE PORTAL
 * --------------------------
 *  - Fereastra de editare porneste de la CONTUL parolei. Butonul vechi trecea
 *    `data-user="<cine a creat-o>"` in casuta "User:", deci un simplu "Update"
 *    scria numele operatorului peste numele contului.
 *  - Dezvaluirea nu se mai face intr-un `swal` cu parola in titlu, ci intr-o
 *    fereastra care spune ca ramane deschisa pana o inchizi. Acelasi gest, cu
 *    parola intr-un loc de unde se poate copia.
 *  - Panoul de filtre nu mai e un sertar care iese din marginea ecranului, ci un
 *    bloc care se desface sub buton. Aceleasi trei filtre.
 *
 * FILTRUL "COMPANY" ARE, CA IN PORTAL, UN SINGUR RASPUNS UTIL: firma aleasa din
 * antet. Lista e deja marginita la ea (`company_id = <firma din antet>`), iar
 * filtrul se adauga PESTE - deci alta firma da o lista goala. Nu s-a schimbat
 * aici: e o hotarare despre ce inseamna ecranul, nu o greseala de mutare.
 */

const TABLE = 'settings-pass-safe'

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** `pass_safeModel` din `public/js/pass_safe.js`, coloana cu coloana. */
function columns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'Description',
      orderable: true,
      visible: true,
      width: 350,
      className: 'a-left',
      html: (row) => esc(String(row.description ?? '')),
    },
    {
      index: 1,
      title: 'User Name',
      orderable: true,
      visible: true,
      width: 250,
      className: 'a-left',
      html: (row) => esc(String(row.username ?? '')),
    },
    {
      index: 2,
      title: 'Creator',
      orderable: true,
      visible: true,
      width: 250,
      className: 'a-left',
      html: (row) => esc(String(row.user ?? '')),
    },
    {
      index: 3,
      title: 'Device Type',
      orderable: true,
      visible: true,
      width: 250,
      className: 'a-left',
      html: (row) => esc(String(row.device_type ?? '')),
    },
    {
      index: 4,
      title: 'Device',
      orderable: true,
      visible: true,
      width: 250,
      className: 'a-left',
      html: (row) =>
        '<span style="background-color: #32767E;color: white;width: 10px;border-radius: 5px"> &nbsp; ' +
        esc(String(row.devices ?? '')) +
        ' &nbsp;  </span>',
    },
    {
      index: 5,
      title: 'Company',
      orderable: true,
      visible: true,
      width: 250,
      className: 'a-left',
      html: (row) => esc(String(row.company ?? '')),
    },
    {
      index: 6,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 170,
      className: 'a-left',
      /* Cele trei butoane, cu aer intre ele. Fiecare statea intr-un
         `<div class="ml-3">`, iar `ml-3` e o clasa de Bootstrap 4 - portalul e
         pe 3 si n-o are deloc, deci spatiul cerut era zero. */
      html: (row) => {
        const id = Number(row.ID)
        const spaced = 'display: inline-block; margin-right: 6px'
        return (
          `<div style="${spaced}"><button type="button" title="Reveal Password" class="btn btn-success btn-xs ps-reveal" data-id="${id}">` +
          '<i class="fa fa-eye" aria-hidden="true"></i></button></div>' +
          `<div style="${spaced}"><button type="button" title="Edit Password" class="btn btn-primary btn-xs ps-edit" data-id="${id}">` +
          '<i class="fas fa-pencil-alt" aria-hidden="true"></i></button></div>' +
          `<div style="display: inline-block"><button type="button" title="Delete Password" class="btn btn-danger btn-xs ps-delete" data-id="${id}">` +
          '<span class="fas fa-trash" aria-hidden="true"></span></button></div>'
        )
      },
    },
  ]
}

/* -------------------------------------------------------------------------
 * Fereastra de parola noua (new_password.js)
 * ---------------------------------------------------------------------- */

/** Semnul fiecarui fel. Aceleasi ca in `DeviceKind`, cat se potrivesc. */
const ICONS: Record<string, string> = {
  network: 'network-wired',
  server: 'server',
  workstation: 'desktop',
  hypervisor: 'cubes',
  printer: 'print',
  storage: 'hdd',
  camera: 'video',
  power: 'plug',
  phone: 'phone',
  mobile: 'mobile-alt',
  iot: 'lightbulb',
  generic: 'key',
}

function iconOf(kind: string): string {
  return ICONS[kind] ?? ICONS.generic
}

function metaOf(hit: DeviceHit): string {
  const bits: string[] = []
  if (hit.ip) bits.push(hit.ip)
  if (hit.mac) bits.push(hit.mac)
  if (hit.os) bits.push(hit.os)
  if (hit.type_name) bits.push(hit.type_name)
  /* Locatia la coada, si numai cand se stie: la un aparat gasit ea vine de la
     releul care l-a vazut, si de multe ori nu vine deloc. */
  if (hit.location) bits.push(hit.location)
  return bits.join(' · ')
}

/**
 * Singurul tip al furnizorului care e de felul asta.
 *
 * Autodetectia da un FEL - "Network device", "Printer" - iar inventarul are
 * tipurile lui: "Switch", "Router", "Imprimanta". Cand exact unul e de felul
 * citit, nu e nimic de ales, si se alege. Cand sunt mai multe, se lasa gol:
 * o ghicitoare pusa in select se salveaza fara sa se uite nimeni la ea.
 */
function onlyTypeOfKind(kind: string, types: Option[], kindByType: Record<string, string>): number {
  if (kind === '' || kind === 'generic') return 0
  let found = 0
  let many = false
  for (const type of types) {
    if (kindByType[String(type.id)] === kind) {
      many = found !== 0
      found = type.id
    }
  }
  return many ? 0 : found
}

function matchTypeByName(name: string, types: Option[]): number {
  if (name === '') return 0
  let found = 0
  for (const type of types) {
    if (type.name.toLowerCase() === name.toLowerCase()) found = type.id
  }
  return found
}

/** De unde stim tipul. Se scrie, ca sa se poata verifica. */
function evidenceOf(hit: DeviceHit, typeId: number, byKind: boolean): { text: string; missing: boolean } {
  if (hit.source === 'inventory') {
    return typeId > 0
      ? { text: 'From the inventory record.', missing: false }
      : { text: 'This device has no type in the inventory. Pick one.', missing: false }
  }

  if (hit.detected === '' || hit.detected === 'Unidentified') {
    return {
      text:
        'The scan could not tell what this is' +
        (hit.evidence ? ` (MAC vendor: ${hit.evidence})` : '') +
        '. Pick the type yourself.',
      missing: true,
    }
  }

  return {
    text:
      `Detected from the scan: ${hit.detected}` +
      (hit.evidence ? ` (MAC vendor: ${hit.evidence})` : '') +
      (typeId > 0 ? (byKind ? ' — only one type here fits, picked it.' : '.') : ' — no type here is named that, pick one.'),
    missing: false,
  }
}

const EMPTY_CRED: CredentialInput = { user: '', password: '', description: '' }

/** Un camp al unui rol, fara sa se piarda ce e scris in celelalte. */
function withPart(
  values: Record<string, CredentialInput>,
  key: string,
  part: keyof CredentialInput,
  value: string,
): Record<string, CredentialInput> {
  const current = values[key] ?? EMPTY_CRED

  return { ...values, [key]: { ...current, [part]: value } }
}

function DeviceTag({ source }: { source: string }) {
  return source === 'inventory' ? (
    <span className="ps-tag ps-tag-inventory">Registered</span>
  ) : (
    <span className="ps-tag ps-tag-discovered">Discovered</span>
  )
}

function NewPasswordModal({
  shell,
  open,
  onClose,
  onSaved,
}: {
  shell: PassSafeShell
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const [office, setOffice] = useState('')
  const [term, setTerm] = useState('')
  const [hits, setHits] = useState<DeviceHit[]>([])
  const [device, setDevice] = useState<DeviceHit | null>(null)
  const [typeId, setTypeId] = useState(0)
  const [byKind, setByKind] = useState(false)
  const [values, setValues] = useState<Record<string, CredentialInput>>({})
  const [shown, setShown] = useState<string[]>([])
  const [problem, setProblem] = useState('')
  const [busy, setBusy] = useState(false)
  const [searching, setSearching] = useState(false)
  /* Cine tasteaza repede cere de cinci ori; raspunsurile se pot intoarce in alta
     ordine decat au plecat, si ultimul scris ar fi fost al unei cautari vechi. */
  const ticket = useRef(0)

  const reset = () => {
    setOffice('')
    setTerm('')
    setHits([])
    setDevice(null)
    setTypeId(0)
    setByKind(false)
    setValues({})
    setShown([])
    setProblem('')
  }

  useEffect(() => {
    if (open) reset()
  }, [open])

  /* Cautarea, cu 250 ms de asteptare dupa ultima litera - ca in new_password.js. */
  useEffect(() => {
    if (!open || device !== null) return
    const timer = setTimeout(() => {
      const mine = ++ticket.current
      setSearching(true)
      searchDevices(term, Number(office || 0))
        .then((answer) => {
          if (mine === ticket.current) setHits(answer.devices)
        })
        .catch(() => {
          if (mine === ticket.current) setHits([])
        })
        .finally(() => {
          if (mine === ticket.current) setSearching(false)
        })
    }, term === '' ? 0 : 250)
    return () => clearTimeout(timer)
  }, [open, term, office, device])

  const kind = typeId > 0 ? (shell.kindByType[String(typeId)] ?? 'generic') : (device?.kind ?? 'generic')
  const roles: CredentialRole[] = shell.profiles[kind] ?? shell.profiles.generic ?? []

  /* Un rol optional se arata numai daca e cerut - fie fiindca omul a apasat pe
     el, fie fiindca a scris deja ceva in el inainte sa schimbe tipul. */
  const open_ = roles.filter(
    (role) => role.required || shown.includes(role.key) || (values[role.key]?.user ?? '') !== '' || (values[role.key]?.password ?? '') !== '',
  )
  const more = roles.filter((role) => !open_.includes(role))

  const choose = (hit: DeviceHit) => {
    setDevice(hit)
    setProblem('')
    let picked = hit.type_id > 0 ? hit.type_id : matchTypeByName(hit.detected, shell.deviceTypes)
    let fromKind = false
    if (picked === 0) {
      picked = onlyTypeOfKind(hit.kind, shell.deviceTypes, shell.kindByType)
      fromKind = picked !== 0
    }
    setTypeId(picked)
    setByKind(fromKind)
  }

  const evidence = device ? evidenceOf(device, typeId, byKind) : null

  const complaint = (): string => {
    if (!device) return 'Pick a device first.'
    if (typeId === 0) return 'Pick the device type.'
    let filled = 0
    let missingUser = ''
    for (const role of open_) {
      const value = values[role.key]
      if (!value || value.password === '') continue
      filled += 1
      if (role.user && (value.user ?? '') === '') missingUser = role.label
    }
    if (filled === 0) return 'Fill in at least one password.'
    if (missingUser !== '') return `The account is missing for: ${missingUser}.`
    return ''
  }

  const save = async () => {
    const said = complaint()
    if (said !== '') {
      setProblem(said)
      return
    }
    setBusy(true)
    setProblem('')
    try {
      const creds: Record<string, CredentialInput> = {}
      for (const role of open_) {
        const value = values[role.key]
        if (!value || value.password === '') continue
        creds[role.key] = {
          user: role.user ? value.user : '',
          password: value.password,
          description: value.description || role.label,
        }
      }
      const answer = await createPasswords({
        device_id: device!.source === 'inventory' ? device!.id : 0,
        discovered_id: device!.source === 'discovered' ? device!.id : 0,
        device_type: typeId,
        creds,
      })
      toast(answer.created === 1 ? 'Password saved.' : `${answer.created} passwords saved.`, 'Success', 'success')
      onSaved()
      onClose()
    } catch (error) {
      /* Fereastra veche spunea "created" si cand modelul intorcea FALSE. Cand
         serverul stie DE CE a refuzat, se scrie motivul lui. */
      setProblem(error instanceof ApiError ? error.message : 'Nothing was saved. Check the device and try again.')
    } finally {
      setBusy(false)
    }
  }

  const field = (key: string, part: keyof CredentialInput, label: string, type: string, hint: string) => (
    <div>
      <label>{label}</label>
      <input
        type={type}
        className="form-control"
        autoComplete={part === 'password' ? 'new-password' : 'off'}
        placeholder={hint}
        value={values[key]?.[part] ?? ''}
        onChange={(event) => setValues((prev) => withPart(prev, key, part, event.target.value))}
      />
    </div>
  )

  return (
    <Modal
      id="newPasswordModal"
      open={open}
      onClose={onClose}
      large
      labelledBy="newPasswordModalLabel"
      title={
        <>
          <i className="fa fa-unlock-alt" aria-hidden="true"></i> New password
        </>
      }
    >
      <div className="ps-new">
        <div className="ps-step">
          <div className="ps-step-head">
            <span className="ps-step-no">1</span> Device
          </div>

          {device === null ? (
            <div>
              <div className="ps-find">
                {/*
                  Locatia strange lista, nu o conditioneaza: la un client cu cinci
                  sute de masini, "doar ce e in Pipera" e diferenta dintre a gasi
                  aparatul si a-l cauta.
                */}
                <select className="form-control" value={office} onChange={(e) => setOffice(e.target.value)}>
                  <option value="">All locations</option>
                  {shell.offices.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <div className="ps-search">
                  <i className="fas fa-search" aria-hidden="true"></i>
                  <input
                    type="text"
                    className="form-control"
                    autoComplete="off"
                    placeholder="Search by name, IP or MAC"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="ps-results">
                {searching && hits.length === 0 ? (
                  <div className="ps-empty">Searching…</div>
                ) : hits.length === 0 ? (
                  <div className="ps-empty">No device found. Try a part of the name, an IP or a MAC.</div>
                ) : (
                  hits.map((hit) => (
                    <div
                      key={`${hit.source}-${hit.id}`}
                      className="ps-hit"
                      role="button"
                      tabIndex={0}
                      onClick={() => choose(hit)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') choose(hit)
                      }}
                    >
                      <i className={`fas fa-${iconOf(hit.kind)} ps-hit-icon`} aria-hidden="true"></i>
                      <div className="ps-hit-body">
                        <div className="ps-hit-name">{hit.name}</div>
                        <div className="ps-hit-meta">{metaOf(hit)}</div>
                      </div>
                      <DeviceTag source={hit.source} />
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="ps-chosen">
              <i className={`fas fa-${iconOf(device.kind)} ps-chosen-icon`} aria-hidden="true"></i>
              <div className="ps-chosen-body">
                <div className="ps-chosen-name">{device.name}</div>
                <div className="ps-chosen-meta">
                  {metaOf(device)} <DeviceTag source={device.source} />
                </div>
              </div>
              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={() => {
                  /* Locatia RAMANE pusa: cine se intoarce sa aleaga alt aparat e,
                     aproape mereu, tot in aceeasi locatie. */
                  setDevice(null)
                  setTerm('')
                  setTypeId(0)
                }}
              >
                Change
              </button>
            </div>
          )}
        </div>

        {device !== null ? (
          <>
            <div className="ps-step">
              <div className="ps-step-head">
                <span className="ps-step-no">2</span> Device type
              </div>
              <select
                className="form-control"
                value={typeId || ''}
                onChange={(e) => {
                  setTypeId(Number(e.target.value || 0))
                  setByKind(false)
                }}
              >
                <option value="">Select..</option>
                {shell.deviceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {evidence ? (
                <p className={evidence.missing ? 'ps-evidence ps-evidence-none' : 'ps-evidence'}>{evidence.text}</p>
              ) : null}
            </div>

            <div className="ps-step">
              <div className="ps-step-head">
                <span className="ps-step-no">3</span> Credentials
              </div>
              <div>
                {open_.map((role) => (
                  <div className="ps-role" key={role.key}>
                    <div className="ps-role-head">
                      <span className="ps-role-label">{role.label}</span>
                      {role.required ? (
                        <span className="ps-role-need">required</span>
                      ) : (
                        <button
                          type="button"
                          className="ps-role-drop"
                          title="Remove"
                          onClick={() => {
                            setShown((prev) => prev.filter((key) => key !== role.key))
                            setValues((prev) => {
                              const next = { ...prev }
                              delete next[role.key]
                              return next
                            })
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div className="ps-role-fields">
                      {role.user ? field(role.key, 'user', role.user_label || 'Account', 'text', role.hint) : null}
                      {field(role.key, 'password', 'Password', 'password', '')}
                      <div>
                        <label>Description</label>
                        <input
                          type="text"
                          className="form-control"
                          value={values[role.key]?.description || role.label}
                          onChange={(event) =>
                            setValues((prev) => withPart(prev, role.key, 'description', event.target.value))
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ps-more">
                {more.map((role) => (
                  <button
                    key={role.key}
                    type="button"
                    className="ps-add"
                    onClick={() => setShown((prev) => [...prev, role.key])}
                  >
                    + {role.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : null}

        {problem !== '' ? <div className="alert alert-danger ps-error">{problem}</div> : null}
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
          Create
        </button>
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Ecranul
 * ---------------------------------------------------------------------- */

type EditForm = { id: number; user: string; description: string; new_password: string }

export function PassSafePage() {
  const client = useQueryClient()
  const shell = useQuery(passSafeShellQuery)
  const [filters, setFilters] = useState<PassSafeFilters>({ deviceType: 0, deviceId: 0, company: 0 })
  const [draft, setDraft] = useState<PassSafeFilters>({ deviceType: 0, deviceId: 0, company: 0 })
  const [panel, setPanel] = useState(false)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [editing, setEditing] = useState<EditForm | null>(null)
  const [revealed, setRevealed] = useState<{ id: number; password: string } | null>(null)
  const [state, setState] = useTableState(TABLE, {
    start: 0,
    length: 25,
    /* `order: [[1, 'desc']]` din pass_safe.js - a doua coloana. */
    order: { column: 1, dir: 'desc' },
    search: '',
  })

  const deviceList = useQuery(devicesOfTypeQuery(draft.deviceType))
  const cols = useMemo(() => columns(), [])
  const table = useQuery({
    ...passSafeTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: PASS_SAFE_ORDER_NAMES[state.order.column] ?? 'description',
      orderDir: state.order.dir,
      search: state.search,
      filters,
    }),
    placeholderData: keepPreviousData,
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const data = shell.data
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'pass_safe'] })
  }

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const reveal = async (id: number) => {
    if (!window.confirm('Are you sure that you want to reveal this password?')) return
    try {
      const answer = await revealPassword(id)
      setRevealed({ id, password: answer.password })
    } catch (error) {
      toast(
        error instanceof ApiError ? error.message : 'You do not have rights to reveal this password.',
        'Error',
        'error',
      )
    }
  }

  const drop = async (id: number) => {
    if (!window.confirm('Delete this password? You will not be able to recover it.')) return
    try {
      await removePassword(id)
      toast('Password deleted', 'Success', 'success')
      refresh()
    } catch (error) {
      toast(
        error instanceof ApiError ? error.message : 'You do not have rights to delete this password.',
        'Error',
        'error',
      )
    }
  }

  const startEdit = (id: number) => {
    const row = (table.data?.data ?? []).find((item) => item.ID === id)
    if (!row) return
    /* Contul parolei, nu numele celui care a scris-o - vezi capul fisierului. */
    setEditing({ id, user: row.username, description: row.description, new_password: '' })
  }

  return (
    /* `od-pass-safe`: carligul regulii din `PassSafePage.css` - 22px sub meniu
       cand lista scoate spatiul paginii (`tk-list-flush`). */
    <div id="content" className="od-pass-safe">
      <div className="pass-safe-toolbar">
        <button type="button" className="btn btn-primary" title="Filters" onClick={() => setPanel((was) => !was)}>
          Filters <i className="fas fa-outdent" aria-hidden="true"></i>
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> Create password
        </button>
        <button type="button" className="btn btn-default" onClick={() => setImporting(true)}>
          <i className="fas fa-magic" aria-hidden="true"></i> Import with AI
        </button>
      </div>

      {panel ? (
        <div className="ps-filters">
          <div className="ps-filters-row">
            <div>
              <label>Device Type:</label>
              <select
                className="form-control"
                value={draft.deviceType || ''}
                onChange={(e) => setDraft({ ...draft, deviceType: Number(e.target.value || 0), deviceId: 0 })}
              >
                <option value="">Select..</option>
                {data.filterDeviceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Device:</label>
              <select
                className="form-control"
                value={draft.deviceId || ''}
                onChange={(e) => setDraft({ ...draft, deviceId: Number(e.target.value || 0) })}
              >
                <option value="">no device selected...</option>
                {(deviceList.data?.devices ?? []).map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Company:</label>
              <select
                className="form-control"
                value={draft.company || ''}
                onChange={(e) => setDraft({ ...draft, company: Number(e.target.value || 0) })}
              >
                <option value="">Select..</option>
                {data.companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="ps-filters-actions">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                setFilters(draft)
                setState({ start: 0 })
              }}
            >
              <i className="fa fa-check" aria-hidden="true"></i> Apply
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => {
                const empty = { deviceType: 0, deviceId: 0, company: 0 }
                setDraft(empty)
                setFilters(empty)
                setState({ start: 0 })
              }}
            >
              Reset
            </button>
          </div>
        </div>
      ) : null}

      {/*
        Un singur ascultator pentru butoanele din coloana Actions: ele sunt HTML
        scris de coloana, ca in DataTables, deci nu au React pe ele - exact cum in
        portal erau prinse cu `onclick=` si cu un `on('click', ...)` delegat.

        NU `page-content-wrapper`: clasa aia vine din `app.css` cu
        `margin-left: 270px` (locul unui rail vechi), iar `settings_console.css`
        o stinge numai pe copilul DIRECT al lui `od-zone-body`. Aici sta in
        `#content`, deci tabela pornea cu 270px mai la dreapta si lasa in stanga
        o coloana goala. In portal tabela sta direct sub bara, pe toata latimea.
      */}
      <div
        className="ps-list"
        onClick={(event) => {
          const button = (event.target as HTMLElement).closest('button')
          if (!button) return
          const id = Number(button.getAttribute('data-id') ?? 0)
          if (id <= 0) return
          if (button.classList.contains('ps-reveal')) void reveal(id)
          if (button.classList.contains('ps-edit')) startEdit(id)
          if (button.classList.contains('ps-delete')) void drop(id)
        }}
      >
        <DataTable
          id={TABLE}
          columns={cols}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
          emptyHint="There are no passwords registred!"
        />
      </div>

      <NewPasswordModal shell={data} open={creating} onClose={() => setCreating(false)} onSaved={refresh} />
      <ImportWithAiModal open={importing} onClose={() => setImporting(false)} onSaved={refresh} />

      <Modal
        id="editPasswordModal"
        open={editing !== null}
        onClose={() => setEditing(null)}
        labelledBy="editPasswordModalLabel"
        title={
          <>
            <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit password details
          </>
        }
      >
        {editing ? (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void (async () => {
                try {
                  await updatePassword(editing.id, {
                    user: editing.user,
                    new_password: editing.new_password,
                    description: editing.description,
                  })
                  toast('Password edited successfully.', 'Success', 'success')
                  setEditing(null)
                  refresh()
                } catch (error) {
                  toast(error instanceof ApiError ? error.message : 'Something went wrong', 'Error', 'error')
                }
              })()
            }}
          >
            <div className="form-group">
              <label className="control-label">User:</label>
              <input
                type="text"
                className="form-control"
                required
                value={editing.user}
                onChange={(e) => setEditing({ ...editing, user: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="control-label">New password:</label>
              <input
                type="password"
                className="form-control"
                autoComplete="new-password"
                value={editing.new_password}
                onChange={(e) => setEditing({ ...editing, new_password: e.target.value })}
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                Leave empty to keep the one already stored.
              </span>
            </div>
            <div className="form-group">
              <label className="control-label">Description</label>
              <input
                type="text"
                className="form-control"
                required
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={() => setEditing(null)}>
                Close
              </button>
              <button type="submit" className="btn btn-primary">
                Update
              </button>
            </div>
          </form>
        ) : null}
      </Modal>

      <Modal
        id="revealPasswordModal"
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        labelledBy="revealPasswordModalLabel"
        title={
          <>
            <i className="fa fa-eye" aria-hidden="true"></i> The password has been revealed
          </>
        }
      >
        <div className="ps-revealed">{revealed?.password}</div>
        <p className="text-muted" style={{ fontSize: 12 }}>
          {/*
            Spus, fiindca dezvaluirea s-a si scris in jurnal: cine a cerut-o,
            cand. Nu e o amenintare, e ce trebuie sa stie omul inainte sa lase
            fereastra deschisa pe un ecran comun.
          */}
          This was written to the vault log, with your name and the time. Close the window when you are done.
        </p>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-default"
            onClick={() => {
              if (revealed) void navigator.clipboard?.writeText(revealed.password)
              toast('Password copied.', 'Copied', 'success')
            }}
          >
            Copy
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setRevealed(null)}>
            Close
          </button>
        </div>
      </Modal>
    </div>
  )
}
